import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickOutside } from '../lib/useClickOutside'
import { TOPBAR_BTN_CLASS } from '../app/topbarButton'
import { LANGUAGE_STORAGE_KEY } from '../i18n'
import { LOCALES } from '../i18n/languageMenuLocales'


/** Same flag-menu control rendered in the top bar (authenticated) and on the
 * login screen — the prototype places an identical instance in both spots. */
export function LanguageMenu() {
  const { i18n, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  useClickOutside(containerRef, () => setOpen(false), open)
  const current = LOCALES.find((l) => l.code === i18n.language) ?? LOCALES[0]

  function select(code: string) {
    // Fire-and-forget, same convention as AppShell's logout call — a failed
    // language switch shouldn't surface an error, just leave the UI as-is.
    i18n.changeLanguage(code).catch(() => {})
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code)
    } catch {
      // localStorage unavailable (private mode, disabled) — language still
      // changes for this session, just doesn't persist.
    }
    setOpen(false)
  }

  // The panel is the prototype's `.lang-menu`: at least 220px wide, and
  // bounded by the window rather than a fixed cap, so all 16 languages show
  // without scrolling on a normal window (MS-996).
  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`${TOPBAR_BTN_CLASS} text-[18px] leading-none`}
        title={t('appShell.language', { name: t(`appShell.locales.${current.code}`) })}
        aria-label={t('appShell.selectLanguage')}
      >
        {current.flag}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 min-w-[220px] max-h-[75vh] overflow-y-auto bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-[var(--shadow-md)] z-50 p-1.5">
          {LOCALES.map((l) => (
            <button
              key={l.code}
              onClick={() => select(l.code)}
              className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-[13px] leading-[normal] text-left text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-hover)] ${
                l.code === i18n.language ? 'font-semibold' : ''
              }`}
            >
              <span className="text-[16px] leading-none">{l.flag}</span>
              <span className="flex-1">{t(`appShell.locales.${l.code}`)}</span>
              {l.code === i18n.language && (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
